import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import gisServicesVideo from "../../../Assets/videos/gis-services.mp4";
import softwareServicesVideo from "../../../Assets/videos/product-dev.mp4";
import consultingServicesVideo from "../../../Assets/videos/consulting-services.mp4";
import printingScanningVideo from "../../../Assets/videos/printing-scanning.mp4";
import HomeNavbar from "./HomeNavbar";

const heroContent = [
  {
    id: 1,
    title: "Comprehensive GIS Services",
    subtitle: "Mapping the world with precision and insight",
    description:
      "Delivering advanced GIS solutions for spatial analysis, mapping, and decision-making across industries.",
    video: gisServicesVideo,
    cta: "Explore GIS Services",
    // Earthy greens and blues for geographic & environmental context
    color: "from-green-200/70 to-sky-200/70",
  },
  {
    id: 2,
    title: "Expert Consulting Services",
    subtitle: "Strategic insights that power success",
    description:
      "Offering professional consulting to help organizations optimize operations, technology, and growth strategies.",
    video: consultingServicesVideo,
    cta: "Learn About Consulting",
    // Confident, professional gold tones for trust and expertise
    color: "from-amber-200/70 to-orange-300/70",
  },
  {
    id: 3,
    title: "Innovative Software Services",
    subtitle: "Custom-built solutions for a connected future",
    description:
      "Providing robust software development, integration, and support to drive digital transformation.",
    video: softwareServicesVideo,
    cta: "Discover Software Solutions",
    // Modern purples and blues for innovation and technology
    color: "from-indigo-200/70 to-blue-200/70",
  },
  {
    id: 4,
    title: "Professional Printing & Scanning Services",
    subtitle: "Precision, quality, and speed for all your document needs",
    description:
      "High-quality printing and scanning services designed for efficiency, accuracy, and professional results.",
    video: printingScanningVideo,
    cta: "Get Printing Services",
    // Clean reds and neutral tones for energy and precision
    color: "from-rose-200/70 to-red-200/70",
  },
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-rotate slides every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroContent.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroContent.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroContent.length) % heroContent.length
    );
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section className="relative w-full h-screen overflow-hidden text-white tracking-wide font-sans">
      <HomeNavbar />

      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.video
            key={currentSlide}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
          >
            <source src={heroContent[currentSlide].video} type="video/mp4" />
            Your browser does not support the video tag.
          </motion.video>
        </AnimatePresence>
      </div>

      {/* Gradient Overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-r ${heroContent[currentSlide].color} via-black/40 to-transparent`}
      ></div>

      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.15)_1px,transparent_0)] bg-[length:20px_20px]"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.span
            key={i}
            className="absolute w-2 h-2 bg-white/20 rounded-full"
            initial={{
              opacity: 0,
              scale: 0,
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1],
              x: [Math.random() * 100, Math.random() * 100 + 50],
              y: [Math.random() * 100, Math.random() * 100 + 50],
            }}
            transition={{
              duration: 10 + Math.random() * 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -50 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  {/* Badge */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="inline-flex items-center px-4 py-2 rounded-full bg-black/10 backdrop-blur-sm border border-white/20 mb-6"
                  >
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                    <span className="text-sm font-sans font-semibold tracking-wider">
                      Where Technology Meets the Earth
                    </span>
                  </motion.div>

                  {/* Title */}
                  <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.7 }}
                    className="text-3xl md:text-5xl lg:text-6xl font-lexend leading-tight mb-4"
                  >
                    {heroContent[currentSlide].title}
                  </motion.h1>

                  {/* Subtitle */}
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.7 }}
                    className="text-xl md:text-2xl text-gray-200 mb-6 leading-relaxed"
                  >
                    {heroContent[currentSlide].subtitle}
                  </motion.p>

                  {/* Description */}
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.7 }}
                    className="text-lg text-gray-300 mb-8 max-w-2xl leading-relaxed"
                  >
                    {heroContent[currentSlide].description}
                  </motion.p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Progress Indicators - Desktop */}
            <div className="hidden lg:flex flex-col items-end space-y-4">
              {heroContent.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => goToSlide(index)}
                  className="text-right group"
                >
                  <div className="flex items-center justify-end space-x-4">
                    <div className="text-white group-hover:text-gray-200 transition-colors">
                      <span className="text-sm font-medium opacity-70 group-hover:opacity-100">
                        0{index + 1}
                      </span>
                    </div>
                    <div className="w-32 bg-white/20 rounded-full h-1 overflow-hidden">
                      <motion.div
                        className="h-full bg-white"
                        initial={{ width: 0 }}
                        animate={{
                          width: currentSlide === index ? "100%" : "0%",
                        }}
                        transition={{ duration: 5 }}
                      />
                    </div>
                    <span
                      className={`font-semibold text-lg transition-all duration-300 ${
                        currentSlide === index ? "text-white" : "text-white/40"
                      } group-hover:text-white/80`}
                    >
                      {item.title.split(" ")[0]}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="hidden absolute left-4 lg:left-8 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 md:flex items-center justify-center hover:bg-white/20 transition-all duration-300"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="hidden absolute right-4 lg:right-8 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 md:flex items-center justify-center hover:bg-white/20 transition-all duration-300"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>

      {/* Mobile Dots Navigation */}
      <div className="lg:hidden absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 z-20">
        {heroContent.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentSlide === index ? "bg-white scale-125" : "bg-white/50"
            }`}
          />
        ))}
      </div>

      {/* Mobile Progress Bar */}
      <div className="lg:hidden absolute bottom-4 left-4 right-4 h-1 bg-white/20 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-white"
          initial={{ width: "0%" }}
          animate={{
            width: `${((currentSlide + 1) / heroContent.length) * 100}%`,
          }}
          transition={{ duration: 5 }}
        />
      </div>
    </section>
  );
}
